// src/pages/coach/Availabilities.jsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Snackbar,
  Alert,
  Divider,
  Tooltip,
  Backdrop,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Paper,
  Badge,
  useMediaQuery,
  IconButton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import BoltIcon from "@mui/icons-material/Bolt";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CloseIcon from "@mui/icons-material/Close";
import {
  LocalizationProvider,
  DateCalendar,
  PickersDay,
} from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useAuthStore } from "../../Store/auth";
import { API_ORIGIN } from "../../config/api";

const API = API_ORIGIN;

const TIME_SLOTS = [
  "06:00 - 07:00",
  "07:00 - 08:00",
  "08:00 - 09:00",
  "09:00 - 10:00",
  "10:00 - 11:00",
  "11:00 - 12:00",
  "12:00 - 13:00",
  "13:00 - 14:00",
  "14:00 - 15:00",
  "15:00 - 16:00",
  "16:00 - 17:00",
  "17:00 - 18:00",
  "18:00 - 19:00",
  "19:00 - 20:00",
  "20:00 - 21:00",
  "21:00 - 22:00",
  "22:00 - 23:00",
  "23:00 - 00:00",
];

// ---- Date helpers (UTC-safe) ----
const toYMD = (d) => {
  const dt = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  return dt.toISOString().slice(0, 10);
};
const startOfUTCDayISO = (d) =>
  new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0)).toISOString();

// ---- Auth header helper ----
const authHeaders = (token) => ({
  headers: {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  },
});

export default function Availabilities() {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [availabilities, setAvailabilities] = useState([]);
  const [snack, setSnack] = useState({ open: false, type: "success", msg: "" });
  const [loading, setLoading] = useState(false);
  const [confirm, setConfirm] = useState({ open: false, type: null, payload: null });

  // ✅ mobile calendar toggle (instead of huge always-on calendar)
  const [showCalendar, setShowCalendar] = useState(false);

  const { token, coachId, logout } = useAuthStore();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const slotsRef = useRef(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/api/Availability/find?coachId=${coachId}`,
        authHeaders(token)
      );
      setAvailabilities(res.data || []);
    } catch (err) {
      if (err?.response?.status === 401) logout();
      setSnack({ open: true, type: "error", msg: "Failed to fetch availabilities." });
    } finally {
      setLoading(false);
    }
  }, [coachId, token, logout]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // --- highlighted dates (days that have any availability)
  const highlightedDays = useMemo(() => {
    const set = new Set();
    for (const a of availabilities || []) {
      const ymd = (a.availableDate || "").slice(0, 10);
      if (ymd) set.add(ymd);
    }
    return set;
  }, [availabilities]);

  // existing slots for selected date
  const existingSlotsForSelected = useMemo(() => {
    if (!selectedDate) return new Set();
    const ymd = toYMD(selectedDate);
    return new Set(
      (availabilities || [])
        .filter((a) => (a.availableDate || "").startsWith(ymd))
        .map((a) => a.timeSlot)
    );
  }, [selectedDate, availabilities]);

  // grouped list by date
  const grouped = useMemo(() => {
    const map = new Map();
    for (const a of availabilities) {
      const ymd = (a.availableDate || "").slice(0, 10);
      if (!map.has(ymd)) map.set(ymd, []);
      map.get(ymd).push(a);
    }
    const entries = [...map.entries()].sort((a, b) => b[0].localeCompare(a[0])); // newest first (better UX)
    return entries.map(([ymd, items]) => ({
      date: ymd,
      items: items.sort((x, y) => x.timeSlot.localeCompare(y.timeSlot)),
    }));
  }, [availabilities]);

  // slot toggling
  const toggleSlot = (slot) => {
    if (existingSlotsForSelected.has(slot)) return;
    setSelectedSlots((prev) =>
      prev.includes(slot) ? prev.filter((s) => s !== slot) : [...prev, slot]
    );
  };

  // quick selects
  const selectRange = (fromHour, toHour) => {
    const pick = TIME_SLOTS.filter((s) => {
      const start = parseInt(s.slice(0, 2), 10);
      return start >= fromHour && start < toHour && !existingSlotsForSelected.has(s);
    });
    setSelectedSlots((prev) => Array.from(new Set([...prev, ...pick])));
  };

  const selectAll = () => {
    const pick = TIME_SLOTS.filter((s) => !existingSlotsForSelected.has(s));
    setSelectedSlots(pick);
  };

  const clearSelection = () => setSelectedSlots([]);

  // create
  const handleSave = async () => {
    if (!selectedDate || selectedSlots.length === 0) {
      setSnack({ open: true, type: "error", msg: "Pick a date and at least one slot." });
      return;
    }

    const dateISO = startOfUTCDayISO(selectedDate);
    const uniqueNew = selectedSlots.filter((s) => !existingSlotsForSelected.has(s));
    if (uniqueNew.length === 0) {
      setSnack({ open: true, type: "info", msg: "All selected slots already exist." });
      return;
    }

    setLoading(true);
    try {
      await Promise.all(
        uniqueNew.map((slot) =>
          axios.post(
            `${API}/api/Availability`,
            { coachId, availableDate: dateISO, timeSlot: slot },
            authHeaders(token)
          )
        )
      );
      setSnack({
        open: true,
        type: "success",
        msg: `Saved ${uniqueNew.length} slot${uniqueNew.length > 1 ? "s" : ""}.`,
      });
      setSelectedSlots([]);
      await fetchAll();
    } catch (err) {
      if (err?.response?.status === 401) logout();
      setSnack({ open: true, type: "error", msg: "Failed to save." });
    } finally {
      setLoading(false);
    }
  };

  // delete
  const requestDeleteOne = (availability) =>
    setConfirm({ open: true, type: "one", payload: availability });

  const requestDeleteDate = (ymd) => setConfirm({ open: true, type: "date", payload: ymd });

  const performDelete = async () => {
    const { type, payload } = confirm;
    setConfirm({ open: false, type: null, payload: null });
    setLoading(true);
    try {
      if (type === "one") {
        await axios.delete(`${API}/api/Availability/${payload.id}`, authHeaders(token));
        setSnack({ open: true, type: "success", msg: "Deleted." });
      } else if (type === "date") {
        const items = availabilities.filter((a) => a.availableDate.startsWith(payload));
        await Promise.all(
          items.map((a) => axios.delete(`${API}/api/Availability/${a.id}`, authHeaders(token)))
        );
        setSnack({ open: true, type: "success", msg: `Deleted all on ${payload}.` });
      }
      await fetchAll();
    } catch (err) {
      if (err?.response?.status === 401) logout();
      setSnack({ open: true, type: "error", msg: "Failed to delete." });
    } finally {
      setLoading(false);
    }
  };

  // min date (no past)
  const today = new Date();
  const minDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  // Custom day dot
  const AvailabilityDay = (props) => {
    const { day, outsideCurrentMonth, ...other } = props;
    const ymd = toYMD(day);
    const hasAvailability = highlightedDays.has(ymd);

    return (
      <Badge
        overlap="circular"
        variant={hasAvailability ? "dot" : "standard"}
        color="success"
        sx={{
          "& .MuiBadge-badge": { transform: "translate(0, 6px)" },
        }}
      >
        <PickersDay day={day} outsideCurrentMonth={outsideCurrentMonth} {...other} />
      </Badge>
    );
  };

  const selectedLabel = selectedDate ? toYMD(selectedDate) : "Pick a date";

  return (
    <Box p={{ xs: 1.5, sm: 3, md: 4 }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 3 },
          borderRadius: 3,
          bgcolor: "rgba(255,255,255,0.9)",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        {/* Header (mobile-first) */}
        <Stack spacing={1.2}>
          <Typography variant={isMobile ? "h5" : "h4"} fontWeight={900}>
            Manage Availability
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Choose a date, select time slots, then save.
          </Typography>

          {/* ✅ Mobile compact date row */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              p: 1.2,
              borderRadius: 2.5,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            <Stack spacing={0.2}>
              <Typography variant="caption" color="text.secondary">
                Selected date
              </Typography>
              <Typography variant="subtitle1" fontWeight={800}>
                {selectedLabel}
              </Typography>
            </Stack>

            <Button
              variant="outlined"
              size="small"
              startIcon={<CalendarMonthIcon />}
              onClick={() => setShowCalendar((v) => !v)}
            >
              {showCalendar ? "Hide" : "Calendar"}
            </Button>
          </Stack>

          {/* ✅ Calendar only when user opens it on mobile */}
          {(!isMobile || showCalendar) && (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box
                sx={{
                  width: "100%",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 3,
                  overflow: "hidden",
                  bgcolor: "background.paper",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 1.5,
                    py: 1,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={800}>
                    Choose date
                  </Typography>
                  {isMobile && (
                    <IconButton size="small" onClick={() => setShowCalendar(false)}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>

                <DateCalendar
                  value={selectedDate}
                  onChange={(d) => {
                    setSelectedDate(d);
                    setSelectedSlots([]);
                    if (isMobile) setShowCalendar(false);
                    setTimeout(() => {
                      slotsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                    }, 120);
                  }}
                  disablePast
                  minDate={minDate}
                  slots={{ day: AvailabilityDay }}
                  sx={{
                    // ✅ make calendar smaller on mobile
                    "& .MuiPickersCalendarHeader-root": { px: 1 },
                    "& .MuiDayCalendar-header": { mx: 1 },
                    "& .MuiDayCalendar-slideTransition": { minHeight: isMobile ? 260 : 300 },
                    "& .MuiPickersDay-root": { width: isMobile ? 34 : 40, height: isMobile ? 34 : 40 },
                  }}
                />

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", px: 1.5, pb: 1 }}
                >
                  Green dot means you already saved availability.
                </Typography>
              </Box>
            </LocalizationProvider>
          )}
        </Stack>

        <Divider sx={{ my: 2 }} />

        {/* ✅ Quick actions: horizontal scroll on mobile */}
        <Box
          sx={{
            display: "flex",
            gap: 1,
            overflowX: "auto",
            pb: 0.5,
            "::-webkit-scrollbar": { height: 6 },
          }}
        >
          <Chip icon={<BoltIcon />} label="Morning" onClick={() => selectRange(6, 12)} variant="outlined" />
          <Chip icon={<BoltIcon />} label="Afternoon" onClick={() => selectRange(12, 18)} variant="outlined" />
          <Chip icon={<BoltIcon />} label="Evening" onClick={() => selectRange(18, 24)} variant="outlined" />
          <Chip icon={<EventRepeatIcon />} label="All" onClick={selectAll} variant="outlined" />
          <Chip icon={<CleaningServicesIcon />} label="Clear" onClick={clearSelection} variant="outlined" />
        </Box>

        {/* Slots */}
        <Box
          ref={slotsRef}
          sx={{
            mt: 2,
            p: { xs: 1.5, sm: 2 },
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            {selectedDate ? `Time slots for ${toYMD(selectedDate)}` : "Pick a date to enable selection"}
          </Typography>

          <Grid container spacing={1}>
            {TIME_SLOTS.map((slot) => {
              const isSaved = existingSlotsForSelected.has(slot);
              const isPicked = selectedSlots.includes(slot);

              return (
                <Grid item xs={6} sm={4} md={3} lg={2} key={slot}>
                  <Chip
                    label={slot}
                    onClick={() => (selectedDate && !isSaved ? toggleSlot(slot) : null)}
                    clickable={!!selectedDate && !isSaved}
                    disabled={!selectedDate || isSaved}
                    variant={isPicked ? "filled" : "outlined"}
                    color={isPicked ? "success" : isSaved ? "default" : "primary"}
                    sx={{
                      width: "100%",
                      fontWeight: 800,
                      py: 2.2,
                      borderRadius: 2,
                      opacity: isSaved ? 0.55 : 1,
                      // ✅ bigger touch target on mobile
                      "& .MuiChip-label": { fontSize: isMobile ? 12 : 13 },
                    }}
                  />
                </Grid>
              );
            })}
          </Grid>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} sx={{ mt: 2 }}>
            <Button
              onClick={handleSave}
              variant="contained"
              size="large"
              disabled={!selectedDate || selectedSlots.length === 0}
              sx={{ borderRadius: 2 }}
            >
              Save {selectedSlots.length > 0 ? `(${selectedSlots.length})` : ""}
            </Button>
            <Button
              onClick={clearSelection}
              variant="outlined"
              disabled={selectedSlots.length === 0}
              sx={{ borderRadius: 2 }}
            >
              Clear selection
            </Button>
          </Stack>
        </Box>

        {/* List by date */}
        <Box mt={3}>
          <Typography variant="h6" fontWeight={900} gutterBottom>
            Your Availabilities
          </Typography>

          {grouped.length === 0 ? (
            <Typography color="text.secondary">No availabilities yet.</Typography>
          ) : (
            grouped.map(({ date, items }) => (
              <Card
                key={date}
                sx={{
                  mb: 1.5,
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 1 }}
                  >
                    <Typography variant="subtitle1" fontWeight={900}>
                      {date}{" "}
                      <Typography component="span" variant="caption" color="text.secondary">
                        ({items.length})
                      </Typography>
                    </Typography>

                    <Tooltip title={`Delete all slots on ${date}`}>
                      <Button
                        color="error"
                        variant="outlined"
                        size="small"
                        onClick={() => requestDeleteDate(date)}
                        startIcon={<DeleteOutlineIcon />}
                        sx={{ borderRadius: 2 }}
                      >
                        Delete
                      </Button>
                    </Tooltip>
                  </Stack>

                  <Grid container spacing={1}>
                    {items.map((a) => (
                      <Grid item key={a.id}>
                        <Chip
                          label={a.timeSlot}
                          variant="outlined"
                          onDelete={() => requestDeleteOne(a)}
                          deleteIcon={<DeleteOutlineIcon />}
                          sx={{ borderRadius: 2 }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            ))
          )}
        </Box>
      </Paper>

      {/* Backdrop Loading */}
      <Backdrop open={loading} sx={{ color: "#fff", zIndex: (t) => t.zIndex.modal + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snack.type}
          variant="filled"
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ width: "100%" }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>

      {/* Confirm dialog */}
      <Dialog open={confirm.open} onClose={() => setConfirm({ open: false, type: null, payload: null })}>
        <DialogTitle>Confirm deletion</DialogTitle>
        <DialogContent>
          <Typography>
            {confirm.type === "date"
              ? `Delete all availabilities on ${confirm.payload}?`
              : "Delete this availability?"}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm({ open: false, type: null, payload: null })}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={performDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
